// src/components/LanguagePicker.js
//
// Pass-7 language UX. The site previously had TWO language affordances —
// an EN | ES toggle in the navbar that only flipped between two of 38
// configured locales (and didn't visibly translate the page), plus a full
// `<LocaleSelector />` dropdown at the bottom of every page. The fix is a
// single up-front prompt: a first-visit modal that asks the user which
// language they'd like to read in, in their own script.
//
// Persistence: the user's choice is saved to localStorage under the
// `518cares-locale-v1` key. The modal does NOT auto-open on subsequent
// visits; the only way to re-open it is to click the globe icon in the
// navbar (see Navbar.js). That gives non-tech users one clear path to
// change languages anytime.
//
// Accessibility: focus-trapped while open, ESC closes, ARIA dialog role,
// and the language rows are real <button>s with focus-visible coral rings.
//
// Token-driven throughout; no hardcoded hex.
import React, { useEffect, useRef, useCallback } from 'react';
import { FiX, FiGlobe, FiChevronDown } from 'react-icons/fi';
import { useLocaleSelector } from '../lib/gt-shim';
import '../styles/LanguagePicker.css';

export const LOCALE_STORAGE_KEY = '518cares-locale-v1';

// Native-name fallbacks for every locale configured in gt.config.json. We
// prefer `getLocaleProperties(code).nativeName` (gt-react's metadata), but
// gt-react sometimes returns the English name for less-common scripts —
// this map is the source of truth for what shows in the picker.
//
// Codes match the gt.config.json `locales` array exactly.
const LOCALE_NATIVE_NAME = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  zh: '中文',
  de: 'Deutsch',
  ar: 'العربية',
  ru: 'Русский',
  pt: 'Português',
  hi: 'हिन्दी',
  ja: '日本語',
  ko: '한국어',
  vi: 'Tiếng Việt',
  it: 'Italiano',
  pl: 'Polski',
  nl: 'Nederlands',
  tr: 'Türkçe',
  fa: 'فارسی',
  he: 'עברית',
  th: 'ไทย',
  uk: 'Українська',
  ro: 'Română',
  hu: 'Magyar',
  sv: 'Svenska',
  el: 'Ελληνικά',
  cs: 'Čeština',
  da: 'Dansk',
  fi: 'Suomi',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  no: 'Norsk',
  sk: 'Slovenčina',
  bg: 'Български',
  hr: 'Hrvatski',
  lt: 'Lietuvių',
  sl: 'Slovenščina',
  sr: 'Српски',
  et: 'Eesti',
  lv: 'Latviešu',
};

// (The per-row English-name column was removed — the native name alone is
// enough for someone choosing their own language, and it cut modal clutter.)

// Featured languages for the Capital District NY: anchored in Albany-area
// demographics (Census + AHCC community-needs reports) — Spanish is the
// largest non-English language by population, then Arabic / Vietnamese /
// Chinese / Russian / Portuguese / French (from the regional refugee
// resettlement and historic French-Canadian populations).
const FEATURED_LOCALES = ['en', 'es', 'fr', 'ar', 'vi', 'zh', 'pt', 'ru'];

// Languages whose script reads right-to-left — used to flip the row
// alignment so the native name reads naturally.
const RTL_LOCALES = new Set(['ar', 'he', 'fa']);

const baseLocale = (l) => (l || 'en').toLowerCase().split(/[-_]/)[0];

const nativeNameFor = (code, getLocaleProperties) => {
  const fromMap = LOCALE_NATIVE_NAME[code];
  if (fromMap) return fromMap;
  // Fallback: gt-react metadata. Returns the English name for some scripts,
  // but it's better than dropping the code raw.
  try {
    const props = getLocaleProperties?.(code);
    if (props?.nativeName) return props.nativeName;
  } catch {
    /* gt-react throws for unknown codes — fall through */
  }
  return code.toUpperCase();
};

const LanguagePicker = ({ open, onClose }) => {
  const { locale, locales, setLocale, getLocaleProperties } = useLocaleSelector();
  const current = baseLocale(locale);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocused = useRef(null);

  // The featured rows are intersected with what's actually configured in
  // the GTProvider so we never offer a locale the runtime can't translate.
  const availableLocales = React.useMemo(() => {
    const set = new Set(['en', ...(locales || [])]);
    return Array.from(set);
  }, [locales]);

  const featuredRows = React.useMemo(
    () => FEATURED_LOCALES.filter((c) => availableLocales.includes(c)),
    [availableLocales],
  );

  const otherRows = React.useMemo(
    () =>
      availableLocales
        .filter((c) => !FEATURED_LOCALES.includes(c))
        .sort((a, b) => {
          const an = nativeNameFor(a, getLocaleProperties);
          const bn = nativeNameFor(b, getLocaleProperties);
          return an.localeCompare(bn);
        }),
    [availableLocales, getLocaleProperties],
  );

  const handleChoose = useCallback(
    (code) => {
      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
      } catch {
        /* private mode / storage disabled — choice still applies for the session */
      }
      // Only call setLocale when actually changing — calling it with the
      // current locale needlessly re-renders the whole translation tree.
      if (baseLocale(code) !== current) {
        setLocale(code);
      }
      setMoreOpen(false);
      onClose?.();
    },
    [setLocale, current, onClose],
  );

  // Focus management: capture the trigger, focus the close button on open,
  // restore focus on close. Trap Tab/Shift+Tab inside the dialog while open.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;

    // Focus the dialog itself first so screen readers announce the prompt.
    const focusTarget = dialogRef.current?.querySelector(
      '[data-language-picker-initial-focus="true"]',
    );
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    // Stop the body from scrolling behind the modal.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
      if (
        previouslyFocused.current &&
        previouslyFocused.current instanceof HTMLElement &&
        document.body.contains(previouslyFocused.current)
      ) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  // The prompt itself stays in BOTH English and Spanish at all times so a
  // first-time visitor in either language immediately recognises what they
  // are being asked. Putting it under <T> would translate it INTO the
  // current locale only — we want the bilingual cue regardless of where
  // the user lands.
  return (
    <div
      className="language-picker-backdrop"
      onClick={(e) => {
        // Backdrop click closes only if the user clicks the dim, not the dialog.
        if (e.target === e.currentTarget) onClose?.();
      }}>
      <div
        ref={dialogRef}
        className="language-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-picker-title"
        tabIndex={-1}>
        <header className="language-picker__header">
          <span className="language-picker__icon" aria-hidden="true">
            <FiGlobe />
          </span>
          <div className="language-picker__prompt">
            <h2
              id="language-picker-title"
              className="language-picker__title">
              What language would you like to use?
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="language-picker__close"
            onClick={() => onClose?.()}
            aria-label="Close language picker">
            <FiX />
          </button>
        </header>

        <ul className="language-picker__list" role="listbox" aria-label="Languages">
          {featuredRows.map((code, idx) => {
            const native = nativeNameFor(code, getLocaleProperties);
            const isCurrent = baseLocale(code) === current;
            return (
              <li key={code} role="option" aria-selected={isCurrent}>
                <button
                  type="button"
                  className={`language-picker__option${isCurrent ? ' is-current' : ''}`}
                  lang={code}
                  dir={RTL_LOCALES.has(code) ? 'rtl' : 'ltr'}
                  data-language-picker-initial-focus={idx === 0 ? 'true' : undefined}
                  onClick={() => handleChoose(code)}>
                  <span className="language-picker__option-native">{native}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="language-picker__more">
          <button
            type="button"
            className="language-picker__more-toggle"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}>
            <span>More languages</span>
            <FiChevronDown
              className={`language-picker__more-chevron${moreOpen ? ' is-open' : ''}`}
              aria-hidden="true" />
          </button>
          {moreOpen && (
            <ul className="language-picker__grid" role="listbox" aria-label="All languages">
              {otherRows.map((code) => {
                const native = nativeNameFor(code, getLocaleProperties);
                const isCurrent = baseLocale(code) === current;
                return (
                  <li key={code} role="option" aria-selected={isCurrent}>
                    <button
                      type="button"
                      className={`language-picker__grid-option${isCurrent ? ' is-current' : ''}`}
                      lang={code}
                      dir={RTL_LOCALES.has(code) ? 'rtl' : 'ltr'}
                      onClick={() => handleChoose(code)}>
                      {native}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="language-picker__footer">
          <button
            type="button"
            className="language-picker__skip"
            onClick={() => handleChoose('en')}>
            Continue in English
          </button>
        </footer>
      </div>
    </div>
  );
};

export default LanguagePicker;
